import { IdentityTypeCode } from '@verifime/utils';
import { ENTITY_TYPE_GROUP_TITLE } from './entityTypeSelectorCopy';

/**
 * Every entity type the customer can choose alongside themselves: all but the individual. A string
 * union (not the enum) so the caller can pass person-api's `EntityType` value as it arrives.
 *
 * Deliberately exactly the seven supported non-individual types. person-api's `EntityType` also
 * has foreign (`Individual_Foreign`, `Private_Foreign_Company`, `Public_Foreign_Company`) and
 * `Charity_Association` values; none of them is a valid entity choice under person-api's choice
 * rules, so none is offered here, and a caller holding one cannot pass it in.
 */
export type TSelectableEntityType = Exclude<`${IdentityTypeCode}`, 'Individual_Domestic'>;

/** The customer's choice: just themselves, or themselves plus one entity. */
export type TEntityChoice = 'individual' | TSelectableEntityType;

export type TEntityTypeGroupKey = keyof typeof ENTITY_TYPE_GROUP_TITLE;

export type TEntityTypeGroup = {
  title: string;
  entityTypes: TSelectableEntityType[];
};

/**
 * The entity groups and the tiles in each, in tile order. The order the groups appear in comes
 * from `DEFAULT_ENTITY_TYPE_GROUP_ORDER` (a caller can override it with `groupOrder`), never from
 * the keys of this record; they are declared in that same order for readability, so a stray
 * `Object.values` would still read Organisation, Trust, Business. The tile order inside a group is
 * fixed.
 */
export const ENTITY_TYPE_GROUPS: Record<TEntityTypeGroupKey, TEntityTypeGroup> = {
  organisation: {
    title: ENTITY_TYPE_GROUP_TITLE.organisation,
    entityTypes: [IdentityTypeCode.Private_Company, IdentityTypeCode.Public_Company],
  },
  trust: {
    title: ENTITY_TYPE_GROUP_TITLE.trust,
    entityTypes: [
      IdentityTypeCode.SMSF,
      IdentityTypeCode.Trust_Unregulated,
      IdentityTypeCode.Trust_Regulated,
    ],
  },
  business: {
    title: ENTITY_TYPE_GROUP_TITLE.business,
    entityTypes: [IdentityTypeCode.Sole_Trader, IdentityTypeCode.Partnership],
  },
};

/**
 * The one order every screen that lists entity types shows the groups in: Organisation, Trust,
 * Business. The tile order inside a group stays as `ENTITY_TYPE_GROUPS` lists it.
 */
export const DEFAULT_ENTITY_TYPE_GROUP_ORDER: readonly TEntityTypeGroupKey[] = [
  'organisation',
  'trust',
  'business',
];
